import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  trainTogetherUpdateStepStatus,
  updateStepStatus,
} from '@cfa/pathway-fe-shared/xp';
import { Button, LoadingIndicator } from '@cfa/react-core';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { invalidateAllPlanQueries } from '@/utils/queryInvalidation';

import GenericError from '@/components/Error/GenericError';

import Constants, { routes } from 'constants/index';
import { isApiError } from 'utils/request';

import { useDocProxyContext } from '@/routes/TrainingPlans/hooks/useDocProxyContext';

const DocumentCompleteButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { planId, stepId, userIds, stepStatus } = useDocProxyContext();

  const {
    mutateAsync: updateTrainTogetherStepStatusMutation,
    isPending: updateTrainTogetherStepStatusLoading,
  } = useMutation({
    mutationFn: async (variables: {
      planId: string;
      stepId: string;
      body: { status: 'COMPLETE'; userIds: string[] };
    }) => {
      const { data } = await trainTogetherUpdateStepStatus({
        path: { planId: variables.planId, stepId: variables.stepId },
        body: variables.body,
      });
      return data;
    },
  });

  const {
    mutateAsync: updatePlanStepStatusMutation,
    isPending: updatePlanStepStatusLoading,
  } = useMutation({
    mutationFn: async (variables: {
      planId: string;
      stepId: string;
      body: { status: 'COMPLETE' };
    }) => {
      const { data } = await updateStepStatus({
        path: { planId: variables.planId, stepId: variables.stepId },
        body: variables.body,
      });
      return data;
    },
  });

  const isLoading =
    updateTrainTogetherStepStatusLoading || updatePlanStepStatusLoading;

  const onCompleteButtonClick = () => {
    if (!planId || !stepId) {
      return;
    }

    const completedPlanId = planId;
    const completedStepId = stepId;

    const handleResponse = (promise: Promise<unknown>) => {
      promise
        .then(async () => {
          if (!isValidPlanId(completedPlanId)) {
            throw new Error('Invalid planId');
          }
          await invalidateAllPlanQueries(completedPlanId);
          if (!userIds) {
            navigate({
              pathname: routes.plans.plan({ planId: completedPlanId }),
            });
          } else {
            navigate(-1);
          }
        })
        .catch((error: unknown) => {
          if (isApiError(error)) {
            datadogRum.addError(error);
            return <GenericError />;
          }
          datadogRum.addError(error);
        });
    };

    if (userIds) {
      handleResponse(
        updateTrainTogetherStepStatusMutation({
          planId: completedPlanId,
          stepId: completedStepId,
          body: {
            status: 'COMPLETE',
            userIds,
          },
        }),
      );
    } else {
      handleResponse(
        updatePlanStepStatusMutation({
          planId: completedPlanId,
          stepId: completedStepId,
          body: {
            status: 'COMPLETE',
          },
        }),
      );
    }
  };

  if (!stepStatus) {
    return null;
  }

  return stepStatus !== Constants.TRAINING_PLANS.COMPLETE ? (
    <Button
      isDisabled={isLoading}
      color="secondary"
      onPressEnd={onCompleteButtonClick}
      data-testid="CompleteDocumentButton"
    >
      {/* Browser translate extensions rewrite button text nodes; React crashes on
          the loading-state re-render (insertBefore). notranslate + remount via key avoid that. */}
      <span
        key={isLoading ? 'loading' : 'idle'}
        className="notranslate inline-flex items-center gap-2"
        translate="no"
      >
        {isLoading && <LoadingIndicator size="sm" />}
        {t(isLoading ? 'Generic.loading' : 'Button.markCompleted')}
      </span>
    </Button>
  ) : (
    <Button
      color="primary"
      isDisabled
      variant="text"
      data-testid="CompletedDocumentButton"
    >
      <span className="notranslate" translate="no">
        {t('Button.completed')}
      </span>
    </Button>
  );
};

export default DocumentCompleteButton;
